// Fuente unica de verdad de la IDENTIDAD VISUAL por agente (graph_id): color +
// icono. Estaba partida en dos (los colores aqui, los iconos en un mapa local de
// ChatThread) y las dos copias derivaron: `editor_skills` tenia icono naranja
// pero ningun color, asi que el Sidebar lo pintaba gris. Ahora todo se declara
// en un sitio: anadir un agente nuevo = una linea en AGENT_STYLES.
//
// El Sidebar y la bandeja de aprobaciones usan el icono/punto solido; las
// tablas y los chips usan la variante suave (fondo tenue + texto a tono).
//
// Los iconos de lucide son valores normales (no JSX), asi que el modulo sigue
// siendo .ts y no arrastra React aqui.

import {
  Bot,
  Code,
  FileText,
  Headset,
  Landmark,
  ReceiptText,
  Wrench,
  type LucideIcon,
} from "lucide-react";

type AgentStyle = {
  /** Clase de fondo solido para el punto del Sidebar (bg-*-500). */
  dot: string;
  /** Clases de chip suave: fondo tenue + texto del mismo tono. */
  chip: string;
  /** Color del icono (text-*-600), a juego con el punto. */
  iconColor: string;
  /** Icono de lucide que identifica al agente. */
  icon: LucideIcon;
};

const AGENT_STYLES: Record<string, AgentStyle> = {
  pedido: {
    dot: "bg-amber-500",
    chip: "bg-amber-100 text-amber-800",
    iconColor: "text-amber-600",
    icon: FileText,
  },
  presupuesto: {
    dot: "bg-cyan-500",
    chip: "bg-cyan-100 text-cyan-800",
    iconColor: "text-cyan-600",
    icon: ReceiptText,
  },
  sac: {
    dot: "bg-emerald-500",
    chip: "bg-emerald-100 text-emerald-800",
    iconColor: "text-emerald-600",
    icon: Headset,
  },
  proforma: {
    dot: "bg-blue-500",
    chip: "bg-blue-100 text-blue-800",
    iconColor: "text-blue-600",
    icon: FileText,
  },
  // factura_compra no es conversacional (no sale en la lista de chats), pero SI
  // en la bandeja de aprobaciones: necesita su propio color para distinguirlo.
  // No tenia icono en el mapa anterior; el generico (Bot) le sirve.
  factura_compra: {
    dot: "bg-violet-500",
    chip: "bg-violet-100 text-violet-800",
    iconColor: "text-violet-600",
    icon: Bot,
  },
  conciliador_norma_43: {
    dot: "bg-teal-500",
    chip: "bg-teal-100 text-teal-800",
    iconColor: "text-teal-600",
    icon: Landmark,
  },
  especificaciones: {
    dot: "bg-indigo-500",
    chip: "bg-indigo-100 text-indigo-800",
    iconColor: "text-indigo-600",
    icon: Code,
  },
  editor_skills: {
    dot: "bg-orange-500",
    chip: "bg-orange-100 text-orange-800",
    iconColor: "text-orange-600",
    icon: Wrench,
  },
};

const DEFAULT_STYLE: AgentStyle = {
  dot: "bg-gray-400",
  chip: "bg-[var(--muted)] text-[var(--foreground)]",
  iconColor: "text-[var(--muted-foreground)]",
  icon: Bot,
};

function styleOf(graphId: string): AgentStyle {
  return AGENT_STYLES[graphId] ?? DEFAULT_STYLE;
}

/** Clase de fondo del punto del Sidebar para un agente. */
export function agentDotClass(graphId: string): string {
  return styleOf(graphId).dot;
}

/** Clases del chip suave (fondo + texto) para un agente. */
export function agentChipClass(graphId: string): string {
  return styleOf(graphId).chip;
}

/** Icono + color del icono de un agente (gris + Bot si no esta declarado). */
export function agentIcon(graphId: string): { icon: LucideIcon; color: string } {
  const style = styleOf(graphId);
  return { icon: style.icon, color: style.iconColor };
}
